<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  item: { type: Object, required: true },
  mobile: { type: Boolean, default: false },
  faded: { type: Boolean, default: false },
});

const emit = defineEmits(['dismiss']);

const { t } = useI18n();

const KIND = {
  success: {
    icon: 'i-lucide-check',
    ring: 'bg-n-teal-3 text-n-teal-11',
    bar: 'bg-n-teal-9',
    role: 'status',
  },
  error: {
    icon: 'i-lucide-x',
    ring: 'bg-n-ruby-3 text-n-ruby-11',
    bar: 'bg-n-ruby-9',
    role: 'alert',
  },
  warning: {
    icon: 'i-lucide-triangle-alert',
    ring: 'bg-n-amber-3 text-n-amber-11',
    bar: 'bg-n-amber-9',
    role: 'status',
  },
  info: {
    icon: 'i-lucide-info',
    ring: 'bg-n-slate-3 text-n-slate-11',
    bar: 'bg-n-slate-9',
    role: 'status',
  },
  loading: {
    icon: 'i-lucide-loader-circle',
    ring: 'bg-n-slate-3 text-n-slate-11',
    bar: '',
    role: 'status',
  },
};

// Sem inline style: a duração da barra vem de classes estáticas (Tailwind
// só gera o que enxerga no código), então arredondamos pra faixa mais perto.
const BAR_DURATION_CLASS = [
  [3000, '[animation-duration:3s]'],
  [4000, '[animation-duration:4s]'],
  [6000, '[animation-duration:6s]'],
  [8000, '[animation-duration:8s]'],
  [10000, '[animation-duration:10s]'],
];

const cfg = computed(() => KIND[props.item.kind] || KIND.info);
const sticky = computed(() => !props.item.duration);
// No celular não há hover pra pausar: 1s a mais.
const duration = computed(() =>
  props.item.duration ? props.item.duration + (props.mobile ? 1000 : 0) : 0
);
const barClass = computed(() => {
  const found = BAR_DURATION_CLASS.find(([ms]) => duration.value <= ms);
  return (found || BAR_DURATION_CLASS[BAR_DURATION_CLASS.length - 1])[1];
});

const title = computed(() =>
  props.item.translate ? t(props.item.title) : props.item.title
);
const detail = computed(() =>
  props.item.translate && props.item.detail
    ? t(props.item.detail)
    : props.item.detail
);
const hasActions = computed(() => props.item.undo || props.item.action);

// Timer com pausa no hover (mesmo que a barra, que pausa por CSS).
let timer = null;
let startedAt = 0;
let remaining = 0;
const paused = ref(false);

const clear = () => {
  if (timer) clearTimeout(timer);
  timer = null;
};
const start = () => {
  clear();
  if (sticky.value) return;
  startedAt = Date.now();
  timer = setTimeout(() => emit('dismiss'), remaining);
};
const reset = () => {
  remaining = duration.value;
  start();
};
const pause = () => {
  if (!timer) return;
  clear();
  remaining -= Date.now() - startedAt;
  paused.value = true;
};
const resume = () => {
  paused.value = false;
  start();
};

const runUndo = () => {
  emit('dismiss');
  props.item.undo?.();
};
const runAction = () => {
  emit('dismiss');
  props.item.action?.onClick?.();
};

// `tick` muda quando o item é atualizado no lugar (dedupe / loading -> ok):
// reinicia o tempo e a barra.
watch(() => [props.item.tick, props.item.kind, props.item.duration], reset);
onMounted(reset);
onBeforeUnmount(clear);
</script>

<template>
  <div
    :role="cfg.role"
    class="group relative grid grid-cols-[auto_1fr_auto] items-start gap-2.5 overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 py-2.5 pl-3 pr-2 shadow-lg pointer-events-auto motion-reduce:animate-none transition-[opacity,transform] duration-200"
    :class="[
      mobile ? 'animate-toast-in-top' : 'animate-toast-in',
      faded ? 'opacity-50 scale-[0.97]' : '',
    ]"
    @mouseenter="pause"
    @mouseleave="resume"
  >
    <span
      class="mt-px grid size-6 place-items-center rounded-full"
      :class="cfg.ring"
    >
      <Icon
        :icon="cfg.icon"
        class="size-3.5"
        :class="{ 'animate-spin': item.kind === 'loading' }"
      />
    </span>

    <div class="min-w-0">
      <div class="text-[13.5px] font-semibold leading-snug text-n-slate-12">
        {{ title }}
        <span v-if="item.count > 1" class="ml-1 text-n-slate-10">
          {{ `×${item.count}` }}
        </span>
      </div>
      <div
        v-if="detail"
        class="mt-0.5 text-[12.5px] leading-snug text-n-slate-11"
      >
        {{ detail }}
      </div>
      <div v-if="hasActions" class="mt-1.5 flex items-center gap-3">
        <button
          v-if="item.undo"
          type="button"
          class="text-[12.5px] font-bold text-n-brand hover:underline"
          @click="runUndo"
        >
          {{ $t('TOAST.UNDO') }}
        </button>
        <router-link
          v-if="item.action && item.action.to"
          :to="item.action.to"
          class="inline-flex items-center gap-1 text-[12.5px] font-bold text-n-brand hover:underline"
          @click="emit('dismiss')"
        >
          {{ item.action.label }}
          <Icon icon="i-lucide-arrow-right" class="size-3" />
        </router-link>
        <button
          v-else-if="item.action"
          type="button"
          class="text-[12.5px] font-bold text-n-brand hover:underline"
          @click="runAction"
        >
          {{ item.action.label }}
        </button>
      </div>
    </div>

    <button
      type="button"
      class="-mt-0.5 grid size-6 place-items-center rounded-md text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12"
      :aria-label="$t('TOAST.CLOSE')"
      @click="emit('dismiss')"
    >
      <Icon icon="i-lucide-x" class="size-3.5" />
    </button>

    <div
      v-if="!sticky"
      class="absolute inset-x-0 bottom-0 h-0.5 bg-n-alpha-2"
      aria-hidden="true"
    >
      <div
        :key="item.tick"
        class="h-full origin-left animate-toast-bar motion-reduce:animate-none group-hover:[animation-play-state:paused]"
        :class="[cfg.bar, barClass]"
      />
    </div>
  </div>
</template>
